import React, { useCallback, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Popover, Typography } from '@mui/material';
import AttachmentIcon from '@mui/icons-material/Attachment';
import { attachmentService, AttachmentVO } from '@/services/auditService/attachment';

export interface AttachmentIconButtonProps {
    targetType: string;
    targetId: number;
    companyId?: number;
    annualId?: number;
    onClick?: () => void;
}

interface PreviewCacheEntry {
    attachment: AttachmentVO | null;
    previewUrl?: string;
}

const previewCache = new Map<string, PreviewCacheEntry>();

function cacheKey(targetType: string, targetId: number) {
    return `${targetType}:${targetId}`;
}

function isImageAttachment(att: AttachmentVO) {
    if (att.mimeType?.startsWith('image/')) return true;
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(att.fileName || '');
}

export function clearAttachmentPreviewCache(targetType?: string, targetId?: number) {
    if (targetType != null && targetId != null) {
        previewCache.delete(cacheKey(targetType, targetId));
        return;
    }
    previewCache.clear();
}

export default function AttachmentIconButton({
    targetType,
    targetId,
    companyId,
    annualId,
    onClick,
}: AttachmentIconButtonProps): React.ReactElement {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [loading, setLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [isImage, setIsImage] = useState(false);
    const [hasAttachment, setHasAttachment] = useState(false);

    const applyCacheEntry = useCallback((entry: PreviewCacheEntry) => {
        if (!entry.attachment) {
            setHasAttachment(false);
            setPreviewUrl(null);
            setFileName(null);
            setIsImage(false);
            return;
        }
        setHasAttachment(true);
        setFileName(entry.attachment.fileName);
        const image = isImageAttachment(entry.attachment);
        setIsImage(image);
        setPreviewUrl(image ? (entry.previewUrl ?? null) : null);
    }, []);

    const handleMouseEnter = useCallback(async () => {
        // Guard NaN/Infinity from Number(non-numeric id) — backend Long cannot parse "NaN"
        if (!Number.isFinite(targetId) || targetId <= 0) return;
        setAnchorEl(buttonRef.current);

        const key = cacheKey(targetType, targetId);
        if (previewCache.has(key)) {
            applyCacheEntry(previewCache.get(key)!);
            return;
        }

        setLoading(true);
        try {
            const list = await attachmentService.listAttachments(targetType, targetId);
            if (list.length === 0) {
                const emptyEntry: PreviewCacheEntry = { attachment: null };
                previewCache.set(key, emptyEntry);
                applyCacheEntry(emptyEntry);
                return;
            }

            const first = list[0];
            let url: string | undefined;
            if (isImageAttachment(first) && companyId && annualId) {
                const blob = await attachmentService.fetchAttachmentBlob({
                    companyId,
                    annualId,
                    fileKey: first.fileKey,
                });
                url = URL.createObjectURL(blob);
            }

            const entry: PreviewCacheEntry = { attachment: first, previewUrl: url };
            previewCache.set(key, entry);
            applyCacheEntry(entry);
        } catch {
            const emptyEntry: PreviewCacheEntry = { attachment: null };
            previewCache.set(key, emptyEntry);
            applyCacheEntry(emptyEntry);
        } finally {
            setLoading(false);
        }
    }, [applyCacheEntry, annualId, companyId, targetId, targetType]);

    const handleMouseLeave = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl) && Number.isFinite(targetId) && targetId > 0 && (loading || hasAttachment);

    const popoverContent = (() => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: 120, minHeight: 80 }}>
                    <CircularProgress size={24} />
                </Box>
            );
        }
        if (!hasAttachment) return null;
        if (isImage && previewUrl) {
            return (
                <Box
                    component="img"
                    src={previewUrl}
                    alt={fileName ?? 'attachment preview'}
                    sx={{ display: 'block', maxWidth: 360, maxHeight: 280, objectFit: 'contain' }}
                />
            );
        }
        return (
            <Typography variant="body2" sx={{ maxWidth: 320, wordBreak: 'break-word' }}>
                {fileName}
            </Typography>
        );
    })();

    return (
        <>
            <IconButton
                ref={buttonRef}
                size="small"
                onClick={onClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <AttachmentIcon fontSize="small" sx={{ maxWidth: 30 }} />
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleMouseLeave}
                disableRestoreFocus
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                sx={{ pointerEvents: 'none' }}
                PaperProps={{ sx: { p: 1, maxWidth: 380 } }}
            >
                {popoverContent}
            </Popover>
        </>
    );
}
